// src/pages/auth/EmailVerificationPage.tsx
import React, { useState, useEffect } from 'react';
import { Mail, CheckCircle, AlertCircle, Loader, ArrowLeft } from 'lucide-react';

interface EmailVerificationPageProps {
  email: string;
  onVerify: (email: string, otp: string) => Promise<void>;
  onResend: (email: string) => Promise<void>;
  onNavigateToLogin?: () => void;
}

export const EmailVerificationPage: React.FC<EmailVerificationPageProps> = ({
  email,
  onVerify,
  onResend,
  onNavigateToLogin
}) => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [canResend, setCanResend] = useState(false);
  const [resendTimer, setResendTimer] = useState(60);

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [resendTimer]);

  const handleCodeChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);

    if (value && index < 5) {
      const nextInput = document.getElementById(`code-${index + 1}`);
      nextInput?.focus();
    }

    if (error) setError(null);
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      const prevInput = document.getElementById(`code-${index - 1}`);
      prevInput?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newCode = [...code];
    pastedData.split('').forEach((digit, i) => {
      if (i < 6) newCode[i] = digit;
    });
    setCode(newCode);
    if (pastedData.length === 6) {
      document.getElementById('code-5')?.focus();
    }
  };

  const handleVerify = async () => {
    const verificationCode = code.join('');
    
    if (verificationCode.length !== 6) {
      setError('Please enter the complete 6-digit code');
      return;
    }

    setIsVerifying(true);
    setError(null);

    try {
      await onVerify(email, verificationCode);
      setSuccess(true);
    } catch (error: any) {
      setError(error.message || 'Verification failed. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    if (!canResend || isResending) return;

    setIsResending(true);
    setError(null);

    try {
      await onResend(email);
      setCanResend(false);
      setResendTimer(60);
      setCode(['', '', '', '', '', '']);
    } catch (error: any) {
      setError(error.message || 'Failed to resend email');
    } finally {
      setIsResending(false);
    }
  };

  if (success) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--color-background)', padding: 'var(--space-6)' }}>
        <div style={{ width: '100%', maxWidth: '400px', textAlign: 'center', backgroundColor: 'var(--color-background)', padding: 'var(--space-8)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)', border: '1px solid var(--color-border)' }}>
          <CheckCircle size={64} color="var(--color-success)" style={{ margin: '0 auto var(--space-6)' }} />
          <h1 style={{ fontFamily: 'var(--font-primary)', fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-bold)', color: 'var(--color-primary)', margin: '0 0 var(--space-4) 0' }}>Email Verified!</h1>
          <p style={{ fontFamily: 'var(--font-primary)', fontSize: 'var(--text-base)', color: 'var(--color-secondary)', margin: '0 0 var(--space-6) 0' }}>Your email has been successfully verified. Redirecting to dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--color-background)', padding: 'var(--space-6)', fontFamily: 'var(--font-primary)' }}>
      <div style={{ width: '100%', maxWidth: '450px', backgroundColor: 'var(--color-background)', padding: 'var(--space-8)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)', border: '1px solid var(--color-border)' }}>
        {onNavigateToLogin && (
          <button onClick={onNavigateToLogin} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', padding: 'var(--space-2)', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-primary)', fontSize: 'var(--text-sm)', color: 'var(--color-secondary)', marginBottom: 'var(--space-6)' }}>
            <ArrowLeft size={16} />Back to Login
          </button>
        )}

        <div style={{ textAlign: 'center', marginBottom: 'var(--space-8)' }}>
          <div style={{ width: '64px', height: '64px', backgroundColor: '#eff6ff', borderRadius: 'var(--radius-full)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto var(--space-4)' }}>
            <Mail size={32} color="#3b82f6" />
          </div>
          <h1 style={{ fontFamily: 'var(--font-primary)', fontSize: 'var(--text-3xl)', fontWeight: 'var(--font-bold)', color: 'var(--color-primary)', margin: '0 0 var(--space-2) 0' }}>Verify Your Email</h1>
          <p style={{ fontFamily: 'var(--font-primary)', fontSize: 'var(--text-base)', color: 'var(--color-secondary)', margin: 0 }}>We sent a 6-digit code to</p>
          <p style={{ fontFamily: 'var(--font-primary)', fontSize: 'var(--text-base)', fontWeight: 'var(--font-semibold)', color: 'var(--color-primary)', margin: 'var(--space-1) 0 0 0' }}>{email}</p>
        </div>

        {error && (
          <div style={{ padding: 'var(--space-3)', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 'var(--radius)', marginBottom: 'var(--space-6)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <AlertCircle size={16} color="var(--color-error)" />
            <span style={{ fontFamily: 'var(--font-primary)', fontSize: 'var(--text-sm)', color: 'var(--color-error)', fontWeight: 'var(--font-medium)' }}>{error}</span>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
          <div>
            <label style={{ display: 'block', fontFamily: 'var(--font-primary)', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)', color: 'var(--color-primary)', marginBottom: 'var(--space-3)', textAlign: 'center' }}>Enter Verification Code</label>
            <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'center' }} onPaste={handlePaste}>
              {code.map((digit, index) => (
                <input
                  key={index}
                  id={`code-${index}`}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleCodeChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  disabled={isVerifying}
                  style={{ width: '56px', height: '56px', textAlign: 'center', fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-bold)', border: `2px solid ${error ? 'var(--color-error)' : 'var(--color-border)'}`, borderRadius: 'var(--radius)', fontFamily: 'var(--font-primary)', backgroundColor: isVerifying ? 'var(--color-surface)' : 'var(--color-background)', color: 'var(--color-primary)', outline: 'none', boxSizing: 'border-box' }}
                  onFocus={(e) => { if (!error) { e.currentTarget.style.borderColor = 'var(--color-primary)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(0, 0, 0, 0.1)'; } }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = error ? 'var(--color-error)' : 'var(--color-border)'; e.currentTarget.style.boxShadow = 'none'; }}
                />
              ))}
            </div>
          </div>

          <button type="button" onClick={handleVerify} disabled={isVerifying || code.some(d => !d)} style={{ width: '100%', padding: 'var(--space-4)', fontFamily: 'var(--font-primary)', fontSize: 'var(--text-base)', fontWeight: 'var(--font-medium)', color: (isVerifying || code.some(d => !d)) ? 'var(--color-muted)' : 'var(--color-background)', backgroundColor: (isVerifying || code.some(d => !d)) ? 'var(--color-surface)' : 'var(--color-primary)', border: 'none', borderRadius: 'var(--radius)', cursor: (isVerifying || code.some(d => !d)) ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-2)' }}>
            {isVerifying && <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />}
            {isVerifying ? 'Verifying...' : 'Verify Email'}
          </button>

          <div style={{ textAlign: 'center' }}>
            <p style={{ fontFamily: 'var(--font-primary)', fontSize: 'var(--text-sm)', color: 'var(--color-secondary)', margin: '0 0 var(--space-2) 0' }}>Didn't receive the code?</p>
            <button type="button" onClick={handleResend} disabled={!canResend || isResending} style={{ padding: 'var(--space-2)', fontFamily: 'var(--font-primary)', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)', color: (canResend && !isResending) ? 'var(--color-primary)' : 'var(--color-muted)', backgroundColor: 'transparent', border: 'none', cursor: (canResend && !isResending) ? 'pointer' : 'not-allowed', textDecoration: 'underline' }}>
              {isResending ? 'Sending...' : canResend ? 'Resend Code' : `Resend in ${resendTimer}s`}
            </button>
          </div>
        </div>
      </div>

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};